// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
// convexParityDiff — pure diff helpers for the Mongo-vs-Convex dual-run (#191).
//
// Used in "both" mode to prove the new backend returns the SAME results as the
// old one (the proof-of-parity approach from the Realm->TanStack migration).
// Pure + import-tested; no React, no I/O.
// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

// Deterministic JSON (sorted keys, recursive) so two equal objects with
// different key orders compare equal.
export function stableStringify(value) {
  if (value === null || typeof value !== "object") return JSON.stringify(value);
  if (Array.isArray(value)) return "[" + value.map(stableStringify).join(",") + "]";
  const keys = Object.keys(value).sort();
  return (
    "{" +
    keys.map((k) => JSON.stringify(k) + ":" + stableStringify(value[k])).join(",") +
    "}"
  );
}

export function idOf(doc) {
  return String(doc?._id ?? doc?.id ?? "");
}

// The frontend `select` transforms add a helper `mongoId` field on read; the
// cached/raw values don't have it, but strip defensively so it never shows as a
// false diff. Same for our bridge bookkeeping.
function normalizeDoc(doc) {
  if (!doc || typeof doc !== "object") return doc;
  const { mongoId, _convexBridge, ...rest } = doc;
  return rest;
}

// Diff two lists of documents keyed by _id/id.
export function diffLists(mongoList, convexList) {
  const m = new Map((mongoList || []).map((d) => [idOf(d), d]));
  const c = new Map((convexList || []).map((d) => [idOf(d), d]));
  const onlyMongo = [...m.keys()].filter((k) => !c.has(k));
  const onlyConvex = [...c.keys()].filter((k) => !m.has(k));
  const fieldMismatchIds = [];
  for (const [k, mv] of m) {
    if (!c.has(k)) continue;
    if (stableStringify(normalizeDoc(mv)) !== stableStringify(normalizeDoc(c.get(k)))) {
      fieldMismatchIds.push(k);
    }
  }
  const match =
    onlyMongo.length === 0 && onlyConvex.length === 0 && fieldMismatchIds.length === 0;
  return {
    match,
    mongoCount: (mongoList || []).length,
    convexCount: (convexList || []).length,
    onlyMongo,
    onlyConvex,
    fieldMismatchIds,
  };
}

// Extract the comparable array from a method's result (handles the
// {projects, meta} shape and single-object lookups).
export function toComparableList(method, result) {
  if (result == null) return [];
  if (Array.isArray(result)) return result;
  if (Array.isArray(result.projects)) return result.projects; // getProjectsByCompanyWithShared
  if (Array.isArray(result.candidates)) return result.candidates;
  if (typeof result === "object") return [result]; // single doc (office meta, user)
  return [];
}

// Compare a Mongo result against a Convex result for one method.
// Returns a record ready to log to Axiom + the Convex comparisons table.
export function compareResults(method, mongoResult, convexResult) {
  const mongoList = toComparableList(method, mongoResult);
  const convexList = toComparableList(method, convexResult);
  const d = diffLists(mongoList, convexList);
  const sample = (arr) => arr.slice(0, 5);
  const diffSummary = d.match
    ? ""
    : [
        `counts m=${d.mongoCount} c=${d.convexCount}`,
        d.onlyMongo.length ? `onlyMongo=${d.onlyMongo.length}${d.onlyMongo.length ? " " + JSON.stringify(sample(d.onlyMongo)) : ""}` : "",
        d.onlyConvex.length ? `onlyConvex=${d.onlyConvex.length} ${JSON.stringify(sample(d.onlyConvex))}` : "",
        d.fieldMismatchIds.length ? `fieldDiffs=${d.fieldMismatchIds.length} ${JSON.stringify(sample(d.fieldMismatchIds))}` : "",
      ]
        .filter(Boolean)
        .join("; ");
  return {
    method,
    match: d.match,
    diffSummary,
    mongoCount: d.mongoCount,
    convexCount: d.convexCount,
  };
}
